import { useEffect, useState } from 'react';
import { makeApiCall } from '../../config/api';

// Shared, module-level cache of /api/insights/capabilities keyed by
// capability_key. Inline AI hints (job duration chip, route risk badges,
// etc.) all consult this, so cache the first fetch for the session instead
// of refetching on every mount.
let cachedMap = null;
let inflight = null;

const fetchCapabilityMap = () => {
  if (cachedMap) return Promise.resolve(cachedMap);
  if (!inflight) {
    inflight = makeApiCall('api/insights/capabilities')
      .then((response) => (response.ok ? response.json() : { capabilities: [] }))
      .then((payload) => {
        cachedMap = Object.fromEntries(
          (payload.capabilities || []).map((capability) => [
            capability.capability_key,
            capability,
          ])
        );
        return cachedMap;
      })
      .catch(() => {
        // Leave the cache empty so a later mount can retry.
        inflight = null;
        return {};
      });
  }
  return inflight;
};

/**
 * Returns the insight capability map ({ capability_key: capability }).
 * Empty object while loading or when the endpoint is unavailable, so
 * callers can safely check `capabilities.some_key?.effective_enabled`.
 */
const useInsightCapabilities = () => {
  const [capabilities, setCapabilities] = useState(cachedMap || {});

  useEffect(() => {
    let mounted = true;
    fetchCapabilityMap().then((map) => {
      if (mounted) setCapabilities(map);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return capabilities;
};

export default useInsightCapabilities;
